import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import '../PageStyle.css';
import ApiSender from '../common/ApiLogic';

// verify-pin response codes the user can retry on this screen: 10301 wrong PIN, 10312 expired code
const RETRYABLE_PIN_CODES = [10301, 10312];

function StcVerifyPage() {

  const otpBoxRef = useRef(null);
  const [goalId, setGoalId] = useState(null);
  const [goalName, setGoalName] = useState(null);
  const nav = useNavigate();
  const location = useLocation();
  const [language, setLanguage] = useState('EN');
  const [msisdn, setMsisdn] = useState(null);
  const [header, setHeader] = useState(false);
  const [antiFraudUniqId, setAntiFraudUniqId] = useState(null);
  const [warning, setWarning] = useState(false);
  const [otp, setOtp] = useState(null);
  const [isGradient, setIsGradient] = useState(false);
  const [paramsObject, setParamsObject] = useState({});
  const [ti, setTi] = useState(null);
  const [ts, setTs] = useState(null);
  const [scriptInjected, setScriptInjected] = useState(false);

  useEffect(() => {
    const data = location.state;
    const msisdnInitial = data.msisdn;
    setMsisdn(msisdnInitial === null ? null : msisdnInitial);

    const antiFraudUniqIdInitial = data.antiFraudUniqId;
    setAntiFraudUniqId(antiFraudUniqIdInitial === null ? null : antiFraudUniqIdInitial);

    const paramsInitial = data?.params ?? {};
    setParamsObject(paramsInitial)

    const headerInitial = data.header;
    setHeader(headerInitial === null ? false : headerInitial);

    const tiInitial = data.ti;
    setTi(tiInitial === null ? null : tiInitial);

    const tsInitial = data.ts;
    setTs(tsInitial === null ? null : tsInitial);
  }, []);

  useEffect(() => {
    const data = location.state;
    const msisdnInitial = data.msisdn;
    const apiSender = new ApiSender();
    async function fetchScript() {
      try {
        const result = await apiSender.loadExternalScript(msisdnInitial, false);
        setTi(result.ti);
        setTs(result.ts);
        setScriptInjected(result.scriptInjected);
      } catch (error) {
        console.error("Error loading external script:", error);
      }
    }
    fetchScript();
  }, []);

  useEffect(() => {
    if (paramsObject && 'campaignId' in paramsObject) {
      const fetchData = async () => {
        try {
          const response = await fetch(process.env.REACT_APP_GOOGLE_BASE_URL + 'campaign/info?uuid=' + paramsObject.campaignId,
            {
              method: 'GET',
              headers: {
                "Content-Type": "application/json"
              }
            });
          if (response.ok) {
            const data = await response.json();
            const status = data.status;
            if (status) {
              setGoalId(data.goalId);
              setGoalName(data.goalName);
            }
          }
        }
        catch (error) {
        }
      }
      fetchData();
    }
  }, [paramsObject]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const lang = urlParams.get('lang');
    if (!lang || (lang.toUpperCase() !== 'EN' && lang.toUpperCase() !== 'AR')) {
      return;
    }
    setLanguage(lang.toUpperCase());
  }, []);

  useEffect(() => {
    if (otpBoxRef.current) {
      otpBoxRef.current.focus();
    }
  }, []);


  const handleLanguage = (event) => {
    const selectedLang = event.target.textContent.trim();
    setLanguage(selectedLang === 'English' ? 'EN' : 'AR');
    setIsGradient(selectedLang === 'English' ? true : false);
  };

  const gradientStyle = {
    background: isGradient
      ? 'linear-gradient(to right, rgba(103, 193, 98, 0.4) 50%, rgba(103, 193, 98, 1) 50%)'
      : 'linear-gradient(to left, rgba(103, 193, 98, 0.4) 50%, rgba(103, 193, 98, 1) 50%)',
  };

  const handleOtpChange = (event) => {
    const newOtp = event.target.value;
    setOtp(newOtp);
  };

  const handleVerifyOtpClick = async () => {
    const o = otp;
    if (o === null || o === 'undefined' || o.length !== 4) {
      document.getElementById('otp-input').style.border = "2px solid rgb(103, 193, 98)";
      return;
    }

    document.getElementById('loading-spinner-verify').style.display = 'inline-flex';
    document.getElementById('verify-btn').disabled = true;
    const campaignId = 'campaignId' in paramsObject ? paramsObject.campaignId : null;
    const trackerId = 'trackerId' in paramsObject ? paramsObject.trackerId : null;
    const apiSender = new ApiSender();
    const result = await apiSender.verifyPin('verify-pin', msisdn, o, campaignId, language, trackerId, goalId, goalName,
      window.location.origin + location.pathname, "STC Main LP", antiFraudUniqId, paramsObject, header, ti, ts);
    if (result.success) {
      nav('/stc/success?lang=' + language);
      return;
    }
    // Only a wrong or expired PIN is retryable on this screen; any other rejection
    // (not eligible, anti-fraud block, already subscribed, HTTP/network error...)
    // ends the flow on the failed page.
    if (!RETRYABLE_PIN_CODES.includes(result.code)) {
      nav('/stc/failed?lang=' + language);
      return;
    }
    setWarning(true);
    setOtp('');
    document.getElementById('warning-msg').textContent = result.message;
    document.getElementById('verify-btn').disabled = false;
    document.getElementById('loading-spinner-verify').style.display = 'none';
  }

  const isOoredoo = () => {
    if (msisdn !== null && msisdn !== undefined) {
      return (msisdn.length === 11 && msisdn.startsWith('9656')) || (msisdn.length === 8 && msisdn.startsWith('6'));
    }
    return false;
  }

  const isStc = () => {
    if (msisdn !== null && msisdn !== undefined) {
      return (msisdn.length === 11 && msisdn.startsWith('9655')) || (msisdn.length === 8 && msisdn.startsWith('5'));
    }
    return false;
  }

  const isZain = () => {
    if (msisdn !== null && msisdn !== undefined) {
      return (msisdn.length === 11 && msisdn.startsWith('9659')) || (msisdn.length === 8 && msisdn.startsWith('9'));
    }
    return false;
  }

  return (
    <div className="Aa3">
      <div className='container'>
        <div className='header'>
          <div className='header-left'>
            <div className="language" style={gradientStyle} onClick={handleLanguage}>
              <div className='language-left'>
                <span>عربي</span>
              </div>
              <div className='language-middle'>
                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path fill="none" strokeWidth="2" d="M12,23 C18.0751322,23 23,18.0751322 23,12 C23,5.92486775 18.0751322,1 12,1 C5.92486775,1 1,5.92486775 1,12 C1,18.0751322 5.92486775,23 12,23 Z M12,23 C15,23 16,18 16,12 C16,6 15,1 12,1 C9,1 8,6 8,12 C8,18 9,23 12,23 Z M2,16 L22,16 M2,8 L22,8"></path></svg>
              </div>
              <div className='language-right'>
                <span>English</span>
              </div>
            </div>
          </div>
          <div className='header-right'>
            <div className='logo'><img alt='Kid Security' src='./../../kid-security-logo.png' /></div>
          </div>
        </div>
        <div className='form-content'>
          <div className='title'>
            <h4 id='headup-title'>{language === 'EN' ? 'Instant Download' : 'تنزيل سريع'}</h4>
          </div>
          <div className='form-content-top'>
            <div className='logo'><img alt='Kid Security' src='./../../phone-icon.png' /></div>
          </div>
          <div className='form-content-middle'>
            <div className='title'>
              <h4 id='title'>{language === 'EN' ? 'Enter your OTP' : 'ادخل الرقم السري'}</h4>
            </div>
            <div id='warning-msg-div' className={`warning-msg ${warning ? 'active' : ''}`}>
              <span id='warning-msg'>{language === 'EN' ? 'Incorrect Number Format' : 'رقم غير صحيح'}</span>
            </div>
            <div style={{ paddingLeft: '0rem' }} className={`input ${warning ? 'vibrate' : ''}`}>
              <input style={{ textAlign: 'center' }} type='tel' value={otp} placeholder='XX XX' autoComplete='one-time-code' minLength={4} maxLength={4} required onChange={handleOtpChange} ref={otpBoxRef} />
            </div>
            <div className='button'>
              <button id='verify-btn' onClick={handleVerifyOtpClick}>
                {language === 'EN' ? 'Verify PIN' : 'تحقق من كلمة المرور'}
                <div id="loading-spinner-verify" className="spinner"></div>
              </button>
              <span>{language === 'EN' ? '' : ''}</span>
            </div>
          </div>
          <div className='form-content-bottom' style={{ direction: language === 'EN' ? "ltr" : "rtl" }}>
            {language === 'EN' ? (
              <span>
                Welcome to Kid Security, the comprehensive parental control app designed to give you total peace of mind by keeping your children safe online through real-time location tracking, screen time management, and intelligent content filtering. This service is available exclusively for STC subscribers: get a 1-day free trial followed by 0.2 KWD per day. Your subscription renews automatically so your protection is never interrupted, but you remain in complete control and can unsubscribe at any time by sending Stop 1 to 50712.
              </span>
            ) : (
              <span>
                مرحبًا بك في Kid Security، التطبيق الشامل للرقابة الأبوية المصمم لمنحك راحة البال الكاملة والحفاظ على أمان أطفالك عبر الإنترنت من خلال تتبع الموقع في الوقت الفعلي، وإدارة وقت الشاشة، وتصفية المحتوى الذكية. هذه الخدمة متاحة حصريًا لمشتركي إس تي سي (STC): احصل على فترة تجريبية مجانية لمدة يوم واحد يليها 0.2 د.ك يوميًا. يتجدد اشتراكك تلقائيًا لضمان استمرار الحماية دون انقطاع، مع احتفاظك بالتحكم الكامل وإمكانية إلغاء الاشتراك في أي وقت عن طريق إرسال Stop 1 إلى 50712.
              </span>
            )}
          </div>
          <div className='form-content-footer'>
            <Link to={`/stc/terms?lang=${language}`}>{language === 'AR' ? (<a>أحكام وشروط</a>) : (<a>Terms and Condition</a>)}</Link>
            <svg fill="#000000" width="1em" height="1em" viewBox="-6.5 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg"><path d="M8.469-1.156h2.25v29.813h-2.25v-29.813z"></path></svg>
            {language === 'AR' ? (<a href='mailto:support@kid-security.io'>للتواصل انقر هنا</a>) : (<a href='mailto:support@kid-security.io'>Contact Us</a>)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StcVerifyPage;
